'use client';
import React from 'react';
import { useEffect, useState } from 'react';
import { useAppStore } from '@/stores/app-store';

/*
 * System Diagram — SCADA-style schematic ของสถาปัตยกรรม MomoSpace จริง.
 * Device column จัดตาม product set (โหมด A completed sets → MQTT · โหมด B project/core → หลาย path).
 * รายละเอียดสินค้า: docs/product-catalog.md · แก้ผังได้ในบล็อก SVG.
 */

type L = { th: string; en: string };
const tx = (v: L, lang: string) => (lang === 'th' ? v.th : v.en);

const panel: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: 20, overflowX: 'auto' };

function SectionTitle({ num, color, title, cap, lang }: { num: string; color: string; title: L; cap: L; lang: string }) {
  return (
    <div style={{ marginTop: 26, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color }}>{num}</span>
        <h3 className="display" style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{tx(title, lang)}</h3>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{tx(cap, lang)}</p>
    </div>
  );
}

/* ── SVG box (SCADA node) ── */
function SBox({ x, y, w, h, color, lines, sub, big, fill }: { x: number; y: number; w: number; h: number; color: string; lines: string[]; sub?: string; big?: boolean; fill?: string }) {
  const midX = x + w / 2;
  const lh = big ? 22 : 14;
  const cy = y + h / 2 + (sub ? -6 : 0);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={9} fill={fill ?? 'var(--bg-deep)'} stroke={color} strokeWidth={big ? 2 : 1.4} />
      {lines.map((ln, i) => (
        <text key={i} x={midX} y={cy - (lines.length - 1) * lh / 2 + i * lh} textAnchor="middle" dominantBaseline="middle" fontSize={big ? 17 : 11.5} fontWeight={big ? 800 : 600} fill="var(--text-primary)">{ln}</text>
      ))}
      {sub && <text x={midX} y={y + h - 9} textAnchor="middle" fontSize={9.5} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">{sub}</text>}
    </g>
  );
}
function Wire({ pts, gold }: { pts: string; dashed?: boolean; gold?: boolean }) {
  // Animated flow: points are ordered source→destination, so the marching dashes
  // travel in the real direction of data flow (CSS keyframe scadaFlow).
  return <polyline className="scada-flow" points={pts} fill="none" stroke={gold ? 'var(--gold)' : 'var(--text-muted)'} strokeWidth={1.6} strokeOpacity={gold ? 0.95 : 0.65} markerEnd={`url(#${gold ? 'arrG' : 'arr'})`} />;
}
/* ── small per-feature chip block ── */
function FeatChip({ x, y, w, label, color, hi }: { x: number; y: number; w: number; label: string; color: string; hi?: boolean }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={24} rx={6} fill={hi ? 'var(--bg-elevated)' : 'var(--bg-deep)'} stroke={color} strokeWidth={hi ? 1.3 : 1} strokeOpacity={hi ? 0.8 : 0.45} />
      <text x={x + w / 2} y={y + 15.5} textAnchor="middle" fontSize={8.5} fontWeight={hi ? 700 : 500} fill={hi ? color : 'var(--text-primary)'} fontFamily="'JetBrains Mono', monospace">{label}</text>
    </g>
  );
}

const BLUE = 'var(--blue)', GREEN = 'var(--green)', CYAN = 'var(--cyan)', PURPLE = 'var(--purple)', AMBER = 'var(--amber)', RED = 'var(--red)', GOLD = 'var(--gold)';

/* ── database pools ── */
const DBS: { name: string; color: string; rows: { code: string; note: L }[] }[] = [
  {
    name: 'momo_momodb', color: 'var(--blue)', rows: [
      { code: 'usertable / usertoken', note: { th: 'ผู้ใช้ + session', en: 'users + session' } },
      { code: 'tbl_config_devices', note: { th: 'อุปกรณ์ + ACL', en: 'devices + ACL' } },
      { code: 'tbl_config_data', note: { th: 'map dataNN → field', en: 'map dataNN → field' } },
      { code: 'usr_<mac_id>', note: { th: 'telemetry 6,800+ ตาราง', en: 'telemetry 6,800+ tables' } },
    ],
  },
  {
    name: 'momo_connect', color: 'var(--red)', rows: [
      { code: 'tbl_cfg_projects_v4', note: { th: 'project + widget JSON', en: 'project + widget JSON' } },
      { code: 'billing / PM', note: { th: 'ไลเซนส์ + บำรุงรักษา', en: 'license + maintenance' } },
      { code: 'notifications', note: { th: 'LINE / Telegram / FCM', en: 'LINE / Telegram / FCM' } },
      { code: 'MQTT / tickets', note: { th: 'เชื่อมต่อ + ซัพพอร์ต', en: 'connect + support' } },
    ],
  },
];

/* ── ecosystem spokes ── */
const SPOKES: { name: string; rel: string; desc: L }[] = [
  { name: 'MomoSpace 3.0', rel: 'consumer', desc: { th: 'อ่าน DB เดียวกัน (production PHP)', en: 'reads same DB (legacy PHP)' } },
  { name: 'Mobile App', rel: 'consumer', desc: { th: 'อ่าน DB เดียวกัน', en: 'reads same DB' } },
  { name: 'momoBLE', rel: 'feeds', desc: { th: 'bridge Minew BLE → MQTT', en: 'Minew BLE bridge → MQTT' } },
  { name: 'Momo ERP', rel: 'billing', desc: { th: 'งานขาย / ใบแจ้งหนี้ / คลัง', en: 'sales / invoice / inventory' } },
  { name: 'Momosight', rel: 'BI', desc: { th: 'ส่ง metric เข้า dashboard ทีมขาย', en: 'push metrics to sales BI' } },
  { name: 'NG-Platform', rel: 'vertical', desc: { th: 'cold-chain วัคซีน', en: 'vaccine cold-chain' } },
];

/** A tenant site card in the Multi-tenant block. */
type SiteCard = { sub: string; siteName: string; isDefault: boolean };
// Built-in fallback if /api/site/list can't be reached (keeps the diagram populated).
const FALLBACK_SITES: SiteCard[] = [
  { sub: 'plant', siteName: 'MomoSpace', isDefault: true },
  { sub: 'm-tech', siteName: 'M-Tech', isDefault: false },
  { sub: 'nex-senses', siteName: 'ENNEX', isDefault: false },
];

type SrvHealth = { server: string; cpu_percent: string; memory_percent: string; load_percent: string; disk_percent: number;
  php_active: number; php_total: number; php_queue: number; db_connections: number; nginx_connections: number;
  apache_connections: number; devices_online: number };
type DbHealth = Record<string, string | number | null>;
type EdgeHealth = { component: string; used: string; capacity: string; state: string; detail: string };

/* A metric reads healthy until it approaches its own ceiling, so the bar is the
   share of capacity used and the dot follows the same thresholds everywhere. */
function tone(pct: number, hard?: string) {
  if (hard && hard !== 'ok' && hard !== 'OK') return RED;
  if (pct >= 90) return RED;
  if (pct >= 75) return AMBER;
  return GREEN;
}
function HealthCard({ title, value, sub, pct, hard }: { title: string; value: string; sub?: string; pct?: number; hard?: string }) {
  const c = tone(pct ?? 0, hard);
  return (
    <div style={{ background: 'var(--bg-deep)', border: '1px solid var(--glass-border)', borderRadius: 10, padding: '10px 12px', minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: c, flexShrink: 0 }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{title}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      {pct !== undefined && (
        <div style={{ height: 3, borderRadius: 99, background: 'var(--glass-border)', marginTop: 7, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, Math.max(2, pct))}%`, height: '100%', background: c }} />
        </div>
      )}
    </div>
  );
}

export default function SystemDiagramPage() {
  const lang = useAppStore((s) => s.lang);

  // Live list of tenant sites (tbl_site_config) so the block never goes stale
  // as new customer sites are added.
  const [sites, setSites] = useState<SiteCard[]>(FALLBACK_SITES);
  useEffect(() => {
    let alive = true;
    fetch('/api/site/list')
      .then((r) => r.json())
      .then((d) => {
        if (alive && Array.isArray(d?.sites) && d.sites.length) setSites(d.sites);
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, []);

  // Live health for the blocks below the topology. Refreshed on a timer because the
  // underlying monitors write once a minute.
  const [srv, setSrv] = useState<SrvHealth[]>([]);
  const [dbh, setDbh] = useState<DbHealth | null>(null);
  const [edge, setEdge] = useState<EdgeHealth[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => fetch('/api/system/health')
      .then((r) => r.json())
      .then((d) => { if (!alive) return; setSrv(d?.servers ?? []); setDbh(d?.db ?? null); setEdge(d?.edge ?? []); })
      .catch(() => { /* keep last good values */ });
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="g-panel">
      <div className="g-header">
        <div>
          <h2 className="g-title">{lang === 'th' ? 'แผนภาพระบบ (SCADA Diagram)' : 'System Diagram (SCADA)'}</h2>
          <p className="g-subtitle">{lang === 'th' ? 'สถาปัตยกรรมจริง — สินค้า (6 กลุ่ม) → ingestion 3 ทาง → DB แชร์ → 3.0 / 4.0 / Mobile' : 'Real topology — products (6 groups) → 3 ingestion paths → shared DB → 3.0 / 4.0 / Mobile'}</p>
        </div>
      </div>

      <div className="g-body" style={{ padding: 24 }}>
        {/* 01 — SCADA topology */}
        <SectionTitle num="01" color={GOLD} lang={lang}
          title={{ th: 'ผังระบบ (System Topology)', en: 'System Topology' }}
          cap={{ th: 'โหมด A (ชุดสำเร็จ) → MQTT · โหมด B (project/core) → แยกตาม controller · เข้า DB กลาง → ทุกผลิตภัณฑ์', en: 'Mode A (completed sets) → MQTT · Mode B (project/core) → by controller · into shared DB → all products' }} />
        <div style={{ ...panel }}>
          <svg viewBox="0 0 1360 640" style={{ width: '100%', minWidth: 960, height: 'auto', fontFamily: 'inherit' }}>
            <defs>
              <marker id="arr" markerWidth="9" markerHeight="9" refX="6.5" refY="3" orient="auto"><path d="M0,0 L6.5,3 L0,6 Z" fill="var(--text-muted)" /></marker>
              <marker id="arrG" markerWidth="9" markerHeight="9" refX="6.5" refY="3" orient="auto"><path d="M0,0 L6.5,3 L0,6 Z" fill="var(--gold)" /></marker>
            </defs>
            <style>{`@keyframes scadaFlow{to{stroke-dashoffset:-14}}.scada-flow{stroke-dasharray:6 6;animation:scadaFlow .9s linear infinite}@media(prefers-reduced-motion:reduce){.scada-flow{animation:none;stroke-dasharray:none}}`}</style>

            {/* column labels */}
            <text x={196} y={40} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">PRODUCTS (DEVICES)</text>
            <text x={675} y={40} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">INGESTION</text>
            <text x={1025} y={40} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">DATABASE</text>
            <text x={1262} y={40} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">PLATFORMS</text>

            {/* section labels */}
            <text x={20} y={80} fontSize={10.5} fontWeight={700} fill={GREEN} fontFamily="'JetBrains Mono', monospace">โหมด A · Completed Set (Self-Serve) → MQTT</text>
            <text x={20} y={324} fontSize={10.5} fontWeight={700} fill={AMBER} fontFamily="'JetBrains Mono', monospace">โหมด B · Project / Core (Custom)</text>

            {/* ── wires (behind boxes) ── */}
            {/* completed sets + Core(MQTT) → junction (554,207) */}
            {[[376, 115], [376, 173], [376, 227], [376, 277], [376, 355]].map((p, i) => (
              <line key={i} className="scada-flow" x1={p[0]} y1={p[1]} x2={554} y2={207} stroke="var(--text-muted)" strokeWidth={1.3} strokeOpacity={0.5} />
            ))}
            <Wire pts="554,207 590,207" />
            {/* MV3/4 → HTTP API momohub · V-BOX → HTTP API momoinfinitech */}
            <Wire pts="376,411 560,398" />
            {/* Minew reports two ways: BLE→gateway→MQTT, and the gateway's own
                HTTP batch POST (momoapi.momohub.net/get2minew). Both are live. */}
            <Wire pts="376,291 470,291 470,385 558,385" />
            <Wire pts="376,464 560,466" />
            {/* data up + correction down (Processor ↔ MQTT Broker) */}
            <Wire pts="655,82 655,60" />
            <Wire pts="685,60 685,82" dashed gold />
            {/* IoT Processor → DB */}
            <Wire pts="760,37 830,37 830,190 900,190" />
            {/* HTTP APIs → DB */}
            <Wire pts="774,398 900,400" />
            <Wire pts="774,466 872,466 872,430 900,430" />
            {/* DB → platforms */}
            <Wire pts="1150,150 1180,107" />
            <Wire pts="1150,290 1180,269" />
            <Wire pts="1150,430 1180,449" />

            {/* ── Mode A: completed sets ── */}
            <SBox x={16} y={90} w={360} h={50} color={AMBER} lines={['Energy Monitoring Set', 'MV2 · PG41 · EM115-WL']} sub="7 items" />
            <SBox x={16} y={148} w={360} h={50} color={GREEN} lines={['Environment / Temperature', 'ESP8266']} sub="6 items" />
            <SBox x={16} y={206} w={360} h={42} color={CYAN} lines={['Water / Utility Set · ESP8266']} sub="5 items" />
            <SBox x={16} y={256} w={360} h={42} color={PURPLE} lines={['Minew Sensor (BLE) → G1-E Gateway']} sub="4 items" />
            {/* ── Mode B: project / core ── */}
            <SBox x={16} y={332} w={360} h={46} color={BLUE} lines={['Core: MV2 · MV5 · MV6', '+ sensor ตามงาน']} />
            <SBox x={16} y={390} w={360} h={42} color={GREEN} lines={['MV3 · MV4 (PiZero-H3)']} />
            <SBox x={16} y={444} w={360} h={40} color={GREEN} lines={['V-BOX E-4G']} />

            {/* ── middle: ingestion ── */}
            <SBox x={590} y={16} w={170} h={42} color={GOLD} lines={['IoT Processor']} fill="var(--bg-elevated)" />
            <SBox x={590} y={82} w={170} h={250} color={PURPLE} lines={['MQTT Broker']} sub="momohub.net" big fill="var(--bg-elevated)" />
            <SBox x={560} y={372} w={214} h={52} color={GREEN} lines={['HTTP API']} sub="momohub.net" />
            <SBox x={560} y={440} w={214} h={52} color={GREEN} lines={['HTTP API']} sub="momoinfinitech.com" />
            {/* Node-RED (momo) — the only consumer of ESP_NODE_MCU_MQTT. Drawn in the
                free band below the HTTP APIs so nothing above has to move. */}
            <Wire pts="590,300 536,300 536,548 558,548" />
            {/* Node-RED posts to both API hosts: post-device-data.php on momohub
                (the URL is built inside the "Momospace API format" function, not in
                the http-request node) and momospace002_api.php on momoinfinitech. */}
            <Wire pts="776,532 812,532 812,436 700,436 700,426" />
            <Wire pts="776,560 790,560 790,504 660,504 660,494" />
            <SBox x={560} y={520} w={214} h={56} color={CYAN} lines={['Node-RED']} sub="momo · ESP_NODE_MCU_MQTT" />
            <text x={706} y={432} fontSize={8} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">post-device-data.php</text>
            <text x={666} y={500} fontSize={8} fill="var(--text-muted)" fontFamily="'JetBrains Mono', monospace">momospace002_api.php</text>
            {/* Second MySQL instance — separate server, not a schema of the one above:
                momo:3307 holds iot-processor's own config plus the Minew HTTP landing table. */}
            <Wire pts="762,37 842,37 842,84 923,84" />
            <SBox x={925} y={58} w={200} h={52} color={CYAN} lines={['momo_iotprocess']} sub="momo:3307" />

            {/* ── database ── */}
            <rect x={900} y={120} width={250} height={380} rx={12} fill="none" stroke={AMBER} strokeWidth={2} />
            <text x={1025} y={146} textAnchor="middle" fontSize={13} fontWeight={800} fill="var(--text-primary)">Database</text>
            <rect x={925} y={160} width={200} height={80} rx={8} fill="var(--bg-deep)" stroke={BLUE} strokeWidth={1.4} />
            <text x={1025} y={200} textAnchor="middle" dominantBaseline="middle" fontSize={13} fontWeight={700} fill="var(--text-primary)" fontFamily="'JetBrains Mono', monospace">momo_momodb</text>
            <rect x={925} y={380} width={200} height={80} rx={8} fill="var(--bg-deep)" stroke={RED} strokeWidth={1.4} />
            <text x={1025} y={420} textAnchor="middle" dominantBaseline="middle" fontSize={13} fontWeight={700} fill="var(--text-primary)" fontFamily="'JetBrains Mono', monospace">momo_connect</text>

            {/* ── platforms + per-feature blocks (connected) ── */}
            {/* 3.0 */}
            <SBox x={1180} y={88} w={175} h={38} color={RED} lines={['Momo Space 3.0']} />
            <line x1={1267} y1={126} x2={1267} y2={134} stroke="var(--text-muted)" strokeWidth={1.3} strokeOpacity={0.55} />
            <FeatChip x={1180} y={134} w={83} label="LINE" color="var(--red)" />
            <FeatChip x={1272} y={134} w={83} label="Telegram" color="var(--red)" />
            <FeatChip x={1180} y={164} w={83} label="Google Sheet" color="var(--red)" />
            <FeatChip x={1272} y={164} w={83} label="REST API" color="var(--red)" />

            {/* 4.0 */}
            <SBox x={1180} y={250} w={175} h={38} color={GOLD} lines={['Momo Space 4.0']} fill="var(--bg-elevated)" />
            <line x1={1267} y1={288} x2={1267} y2={296} stroke="var(--gold)" strokeWidth={1.3} strokeOpacity={0.7} />
            <FeatChip x={1180} y={296} w={83} label="LINE" color="var(--gold)" />
            <FeatChip x={1272} y={296} w={83} label="Telegram" color="var(--gold)" />
            <FeatChip x={1180} y={326} w={83} label="Google Sheet" color="var(--gold)" />
            <FeatChip x={1272} y={326} w={83} label="REST API" color="var(--gold)" />
            <FeatChip x={1180} y={356} w={83} label="AI inside" color="var(--gold)" hi />
            <FeatChip x={1272} y={356} w={83} label="Daily AI report" color="var(--gold)" hi />

            {/* Mobile */}
            <SBox x={1180} y={430} w={175} h={38} color={RED} lines={['Momo Space Mobile app']} />
            <line x1={1267} y1={468} x2={1267} y2={476} stroke="var(--text-muted)" strokeWidth={1.3} strokeOpacity={0.55} />
            <FeatChip x={1180} y={476} w={175} label="Push notification (in-app)" color="var(--red)" />
          </svg>
          {/* Live health — reads the monitoring that already runs (server_health_history,
              server2_db_health_history, tbl_edge_health). Purely additive to the topology. */}
          <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {srv.map((v) => {
              const php = v.php_total ? (v.php_active / v.php_total) * 100 : 0;
              const host = v.server === 'server1' ? 'momohub' : 'momo';
              return (
                <React.Fragment key={v.server}>
                  <HealthCard title={`${host} · CPU`} value={`${Number(v.cpu_percent).toFixed(0)}%`}
                    sub={`load ${Number(v.load_percent).toFixed(0)}% · mem ${Number(v.memory_percent).toFixed(0)}%`}
                    pct={Number(v.cpu_percent)} />
                  <HealthCard title={`${host} · DISK`} value={`${v.disk_percent}%`} pct={Number(v.disk_percent)} />
                  <HealthCard title={`${host} · PHP-FPM`} value={`${v.php_active}/${v.php_total}`}
                    sub={`queue ${v.php_queue}`} pct={php} />
                  <HealthCard title={`${host} · WEB`} value={`nginx ${v.nginx_connections} · apache ${v.apache_connections}`}
                    sub={`db conn ${v.db_connections}`} />
                </React.Fragment>
              );
            })}
            {dbh && (['replication', 'erp', 'legacy'] as const).map((k) => (
              <HealthCard key={k} title={`DB · ${k.toUpperCase()}`}
                value={`${dbh[`${k}_latency_ms`] ?? '?'} ms`}
                sub={`${dbh[`${k}_status`] ?? '?'} · ${dbh[`${k}_processlist`] ?? 0} conn`}
                hard={String(dbh[`${k}_status`] ?? '')} />
            ))}
            {edge.map((e) => {
              const cap = Number(e.capacity) || 0;
              const pct = cap ? (Number(e.used) / cap) * 100 : 0;
              const label = e.component === 'pipeline_lag' ? 'ESP PIPELINE' : 'NODE-RED RAM';
              const val = e.component === 'pipeline_lag' ? `${Number(e.used).toFixed(1)} min` : `${Number(e.used).toFixed(0)}/${cap} MB`;
              return <HealthCard key={e.component} title={label} value={val} sub={e.detail} pct={pct} hard={e.state} />;
            })}
          </div>

          {/* legend + notes */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 12, fontSize: 11.5, color: 'var(--text-muted)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><svg width="26" height="8"><line x1="0" y1="4" x2="26" y2="4" stroke="var(--text-muted)" strokeWidth="1.5" /></svg>{lang === 'th' ? 'ข้อมูลขึ้น' : 'data up'}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><svg width="26" height="8"><line x1="0" y1="4" x2="26" y2="4" stroke="var(--gold)" strokeWidth="1.5" strokeDasharray="4 3" /></svg>{lang === 'th' ? 'feedback: Processor → MQTT Broker → อุปกรณ์ · ESP_CORR_VALUE/{mac}/{field_no}' : 'feedback: Processor → MQTT Broker → device · ESP_CORR_VALUE/{mac}/{field_no}'}</span>
          </div>
          <p style={{ marginTop: 8, fontSize: 11.5, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--blue)', background: 'color-mix(in srgb, var(--blue) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--blue) 28%, transparent)', padding: '2px 8px', borderRadius: 99, fontFamily: "'JetBrains Mono', monospace" }}>SOLUTION</span>
            {lang === 'th' ? 'Energy / Solar / Cold Chain / Water ฯลฯ = เลือกตอน "สร้าง Plant" ใน 4.0 (ไม่ใช่ property ของสินค้า) → เสา ③ Solution Packs' : 'Energy / Solar / Cold Chain / Water etc. = chosen at "create Plant" in 4.0 (not a product property) → pillar ③ Solution Packs'}
          </p>
          <p style={{ marginTop: 6, fontSize: 10.5, color: 'var(--text-muted)' }}>
            {lang === 'th' ? '* รายละเอียดสินค้าแต่ละกลุ่ม: docs/product-catalog.md · ชื่อ DB ต้องยืนยัน (env = momo_connect)' : '* per-group product detail: docs/product-catalog.md · confirm DB name (env = momo_connect)'}
          </p>
        </div>

        {/* 02 — Database detail */}
        <SectionTitle num="02" color={AMBER} lang={lang}
          title={{ th: 'ฐานข้อมูล — 2 pool (รายละเอียด)', en: 'Database — 2 Pools (detail)' }}
          cap={{ th: 'DB แชร์ที่ทั้ง 3.0 / 4.0 / Mobile ใช้ร่วมกัน', en: 'shared DB used by 3.0 / 4.0 / Mobile' }} />
        <div style={{ ...panel }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, minWidth: 0 }}>
            {DBS.map((db, i) => (
              <div key={i} style={{ border: '1px solid var(--glass-border)', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: '#fff', background: db.color }}>{db.name}</div>
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 7, background: 'var(--bg-deep)' }}>
                  {db.rows.map((r, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{r.code}</code>
                      <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>{tx(r.note, lang)}</small>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 03 — Multi-tenant */}
        <SectionTitle num="03" color={PURPLE} lang={lang}
          title={{ th: 'Multi-tenant — Site Manager', en: 'Multi-tenant — Site Manager' }}
          cap={{ th: 'โค้ดชุดเดียว เสิร์ฟหลายลูกค้า แยก subdomain/โลโก้/เมนู ต่อไซต์', en: 'one codebase, many customers — per-site subdomain/logo/menu' }} />
        <div style={{ ...panel }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 480 }}>
            <div style={{ border: '1px solid var(--blue)', background: 'color-mix(in srgb, var(--blue) 10%, transparent)', borderRadius: 11, padding: '10px 16px', textAlign: 'center' }}>
              <div style={{ fontWeight: 650, fontSize: 13.5, color: 'var(--text-primary)' }}>Site Manager</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: 'var(--text-muted)' }}>admin.momoagentix.com</div>
            </div>
            <div style={{ width: 2, height: 18, background: 'var(--glass-border)' }} />
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
              {sites.map((site, i) => {
                // subtitle: default flagged; long names trimmed to keep cards tidy.
                const label = site.siteName.length > 22 ? site.siteName.slice(0, 21).trimEnd() + '…' : site.siteName;
                const sub = site.isDefault ? `${label || 'MomoSpace'} · default` : (label || site.sub);
                return (
                  <div key={site.sub || i} title={site.siteName} style={{ border: '1px solid var(--glass-border)', borderRadius: 11, padding: '10px 15px', background: 'var(--bg-deep)', textAlign: 'center', maxWidth: 200 }}>
                    <div style={{ fontWeight: 650, fontSize: 13.5, color: 'var(--amber)' }}>{site.sub}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ width: 2, height: 18, background: 'var(--glass-border)' }} />
            <div style={{ border: '1px solid var(--glass-border)', borderRadius: 11, padding: '10px 16px', background: 'var(--bg-deep)', textAlign: 'center' }}>
              <div style={{ fontWeight: 650, fontSize: 13.5, color: 'var(--text-primary)' }}>Plants → Devices</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: 'var(--text-muted)' }}>{lang === 'th' ? 'แต่ละไซต์: branding + เมนู + ACL ของตัวเอง' : 'each site: own branding + menu + ACL'}</div>
            </div>
          </div>
        </div>

        {/* 04 — Ecosystem */}
        <SectionTitle num="04" color={RED} lang={lang}
          title={{ th: 'Ecosystem — ระบบรอบตัว', en: 'Ecosystem' }}
          cap={{ th: 'MomoSpace 4.0 เป็นศูนย์กลาง เชื่อมกับระบบอื่นในเครือ Momo', en: 'MomoSpace 4.0 as the hub of the Momo ecosystem' }} />
        <div style={{ ...panel }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ border: '1.5px solid var(--gold)', borderRadius: 14, padding: '13px 26px', textAlign: 'center', background: 'linear-gradient(135deg, color-mix(in srgb, var(--gold) 16%, var(--bg-elevated)), var(--bg-elevated))' }}>
              <div style={{ fontWeight: 750, fontSize: 16, color: 'var(--text-primary)' }}>MomoSpace 4.0</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{lang === 'th' ? 'platform กลาง' : 'central platform'}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>
            {SPOKES.map((sp, i) => (
              <div key={i} style={{ border: '1px solid var(--glass-border)', borderRadius: 12, padding: '12px 14px', background: 'var(--bg-deep)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 650, fontSize: 13.5, color: 'var(--text-primary)' }}>{sp.name}</span>
                  <span style={{ marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, color: 'var(--gold)', border: '1px solid color-mix(in srgb, var(--gold) 30%, transparent)', padding: '1px 7px', borderRadius: 99 }}>{sp.rel}</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>{tx(sp.desc, lang)}</div>
              </div>
            ))}
          </div>
        </div>

        <p style={{ marginTop: 20, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
          {lang === 'th' ? 'อ้างอิง docs/architecture-blueprint.md · docs/product-catalog.md · แก้ได้ที่ components/pages/SystemDiagramPage.tsx' : 'ref docs/architecture-blueprint.md · docs/product-catalog.md · edit components/pages/SystemDiagramPage.tsx'}
        </p>
      </div>
    </div>
  );
}
